import { AdaEmbed, AdaSettings } from 'Shared/ada/adaApi';
import { StradaChat, StradaSettings } from 'Shared/strada/stradaApi';
import { isPresent } from 'Shared/utils/object';

type FSOperations = {
  identify: (userId: string, customData: object) => void;
  setVars: (scope: string, data: object) => void;
  event: (name: string, properties: object) => void;
};

type FSAPI = FSOperations;

declare global {
  interface Window {
    env: any;
    fullstory?: {
      load(fullstoryId: string): void;
    };
    FS?: FSAPI;
    MSInputMethodContext: object;
    zE?(
      target: 'webWidget' | 'webWidget:on' | 'webWidget:get' | 'messenger',
      action: string,
      args?: any
    ): string | void;
    adaEmbed?: AdaEmbed;
    adaSettings?: AdaSettings;
    stradaChat?: StradaChat;
    stradaSettings?: StradaSettings;
    zEmbed?: { widget: 'messenger' | 'classic' };
    AdyenCheckout: any;
    $: any;
    _sva: {
      // https://developers.survicate.com/javascript/methods/#set-visitor-traits
      setVisitorTraits(traits: Record<string, string>): void;
    };
  }

  interface Document {
    documentMode: string;
  }
}

type AdyenEnv = {
  adyenEnv: string;
  adyenClientKey: string;
  adyenVersion: string;
  adyenCardAvsMode: 'none' | 'partial' | 'full';
  adyenCssHash: string;
  adyenScriptHash: string;
};

type Env = {
  auth0Domain: string;
  auth0ClientId: string;
  auth0RedirectUrl: string;
  auth0Audience: string;
  reznorHostname: string;
  ryersonHostname: string;
  ryersonWebHostname: string;
  varysHostname: string;
  rewardsHostname: string;
  incentiveProgramsHostname: string;
  pmwHostname: string;
  sentryEnvironment: string;
  fullstoryId: string;
  proconEnabled: boolean;
  adaChatEnabled: boolean;
  stradaOrganizationId: string;
  stradaAgentId: string;
  datadogEnv: string;
  agentTestPagesEnabled: boolean;
  jaggerUrl: string;
  smartyStreetsId: string;
  stradaEnabled: boolean;
  adasFeaturesEnabled: boolean;
  partnerCommissionsAdminMenuEnabled: boolean;
  oneInc3ExternalLink: string;
  oneInc4ExternalLink: string;
  processOneScriptUrl: string;
  versionNumber: string;
  maintenancePageFlag: boolean;
  survicateEnvironment: 'staging' | 'uat' | 'partner' | 'production';
  rewardsCenterChallengesEnabled: boolean;
  rewardsSpinEnabled: boolean;
  rewardsMockEnabled: boolean;
  rewardsMockAct3: boolean;
  vehicleNewQuestionsEnabled: boolean;
  driverNewQuestionsEnabled: boolean;
  updateCoveragesWithoutHealing: boolean;
  paymentFlexibilityEveryTwoWeeks: boolean;
  paymentFlexibilityDownpaymentSlider: boolean;
  paymentsAndBillingNewDesign: boolean;
  independentAgentStates: string;
} & AdyenEnv;

var defaultAdyenTestConfig: AdyenEnv = {
  adyenEnv: 'test',
  adyenClientKey: 'test_XYYS2G36KFCELMSLEMVWDKDOXQBD7KY5',
  adyenVersion: '5.51.0',
  adyenCardAvsMode: 'none',
  adyenCssHash:
    'sha384-k6qYnmoHaucEm97eQQAs3MK5e44JQ2sksrue2tfdDOnnedKzc0VYQwxzdYWZu8Mj',
  adyenScriptHash:
    'sha384-FJWX32BY0zx3KKed9gdiWxoEAEsA3uh1ixchmdkflgtcDo+SoYg5ZD6uqvDmnafO',
};

const defaults: { [envName: string]: Env } = {
  local: {
    auth0Domain: 'clearcover-dev.auth0.com',
    auth0ClientId: 'EO5fMh3s5TeTauaUViVGK7mMDIt55iN1',
    auth0RedirectUrl: 'http://app.localhost:3002/auth',
    auth0Audience: 'http://api.localhost:3000',
    reznorHostname: 'http://api.localhost:3000',
    ryersonHostname: 'http://localhost:8080',
    ryersonWebHostname: 'http://api.localhost:3008',
    varysHostname: 'http://api.localhost:3006',
    rewardsHostname: 'http://localhost:8080',
    incentiveProgramsHostname: '',
    pmwHostname: 'https://pmw-api.clearcover.systems',
    sentryEnvironment: 'dev',
    fullstoryId: '',
    proconEnabled: process.env.NODE_ENV === 'development',
    adaChatEnabled: false,
    stradaOrganizationId: '',
    stradaAgentId: '',
    datadogEnv: 'dev',
    agentTestPagesEnabled: true,
    jaggerUrl: 'https://app.clearcoverstaging.com/agent/apply',
    ...defaultAdyenTestConfig,
    smartyStreetsId: '',
    stradaEnabled: true,
    adasFeaturesEnabled: true,
    partnerCommissionsAdminMenuEnabled: true,
    oneInc3ExternalLink: 'https://policyone-clearcover-stg.oneincsystems.com',
    oneInc4ExternalLink: 'https://policyone-clearcover-test2.oneincsystems.com',
    processOneScriptUrl: 'https://testportalone.processonepayments.com',
    versionNumber: '',
    maintenancePageFlag: false,
    survicateEnvironment: 'staging',
    rewardsCenterChallengesEnabled: false,
    rewardsSpinEnabled: false,
    rewardsMockEnabled: false,
    rewardsMockAct3: false,
    vehicleNewQuestionsEnabled: false,
    driverNewQuestionsEnabled: false,
    updateCoveragesWithoutHealing: true,
    paymentFlexibilityEveryTwoWeeks: false,
    paymentFlexibilityDownpaymentSlider: false,
    paymentsAndBillingNewDesign: false,
    independentAgentStates: '',
  },
  staging: {
    auth0Domain: 'clearcover-staging.auth0.com',
    auth0ClientId: 'tCKMiT1jAUjnaX5IJA6ud42j0woQY1ci',
    auth0RedirectUrl: 'http://app.localhost:3002/auth',
    auth0Audience: 'https://agency-services.clearcover.com',
    reznorHostname: 'https://api.clearcoverstaging.com',
    ryersonHostname: 'https://product-config.clearcover.systems',
    ryersonWebHostname: 'https://product-config.clearcover.systems',
    // varysHostname: 'http://api.localhost:3006',
    varysHostname: 'https://policy-frontend-api.clearcover.systems',
    rewardsHostname: 'https://apihub.clearcover.systems',
    incentiveProgramsHostname: 'https://vas.clearcover.systems',
    pmwHostname: 'https://pmw-api.clearcover.systems',
    sentryEnvironment: 'dev',
    fullstoryId: '',
    proconEnabled: process.env.NODE_ENV === 'development',
    adaChatEnabled: false,
    stradaOrganizationId: '',
    stradaAgentId: '',
    datadogEnv: 'dev',
    agentTestPagesEnabled: true,
    jaggerUrl: 'https://app.clearcoverstaging.com/agent/apply',
    ...defaultAdyenTestConfig,
    smartyStreetsId: '3082863536969411',
    stradaEnabled: true,
    adasFeaturesEnabled: true,
    partnerCommissionsAdminMenuEnabled: true,
    oneInc3ExternalLink: 'https://policyone-clearcover-stg.oneincsystems.com',
    oneInc4ExternalLink: 'https://policyone-clearcover-test2.oneincsystems.com',
    processOneScriptUrl: 'https://testportalone.processonepayments.com',
    versionNumber: '',
    maintenancePageFlag: false,
    survicateEnvironment: 'staging',
    rewardsCenterChallengesEnabled: false,
    rewardsSpinEnabled: false,
    rewardsMockEnabled: false,
    rewardsMockAct3: false,
    vehicleNewQuestionsEnabled: true,
    driverNewQuestionsEnabled: true,
    updateCoveragesWithoutHealing: true,
    paymentFlexibilityEveryTwoWeeks: false,
    paymentFlexibilityDownpaymentSlider: false,
    paymentsAndBillingNewDesign: true,
    independentAgentStates: '',
  },
  uat: {
    auth0Domain: 'clearcover-uat.auth0.com',
    auth0ClientId: '0OObGpIUw7saLalFHutPWYLG9hoh0lCY',
    auth0RedirectUrl: 'http://app.localhost:3002/auth',
    auth0Audience: 'https://agency-services.clearcover.com',
    reznorHostname: 'https://api.clearcoveruat.com',
    ryersonHostname: '',
    ryersonWebHostname: '',
    varysHostname: 'https://policy-frontend-api.uat.clearcover.systems/',
    rewardsHostname: 'https://apihub.uat.clearcover.systems',
    incentiveProgramsHostname: 'https://vas.uat.clearcover.systems',
    pmwHostname: 'https://pmw-api.clearcover.systems',
    sentryEnvironment: 'dev',
    fullstoryId: '',
    proconEnabled: process.env.NODE_ENV === 'development',
    adaChatEnabled: false,
    stradaOrganizationId: '',
    stradaAgentId: '',
    datadogEnv: 'dev',
    agentTestPagesEnabled: true,
    jaggerUrl: 'https://app.clearcoveruat.com/agent/apply',
    ...defaultAdyenTestConfig,
    smartyStreetsId: '',
    stradaEnabled: false,
    adasFeaturesEnabled: false,
    partnerCommissionsAdminMenuEnabled: false,
    oneInc3ExternalLink: 'https://policyone-clearcover-stg.oneincsystems.com',
    oneInc4ExternalLink: 'https://policyone-clearcover-test2.oneincsystems.com',
    processOneScriptUrl: 'https://testportalone.processonepayments.com',
    versionNumber: '',
    maintenancePageFlag: false,
    survicateEnvironment: 'uat',
    rewardsCenterChallengesEnabled: false,
    rewardsSpinEnabled: false,
    rewardsMockEnabled: false,
    rewardsMockAct3: false,
    vehicleNewQuestionsEnabled: false,
    driverNewQuestionsEnabled: false,
    updateCoveragesWithoutHealing: true,
    paymentFlexibilityEveryTwoWeeks: false,
    paymentFlexibilityDownpaymentSlider: false,
    paymentsAndBillingNewDesign: false,
    independentAgentStates: '',
  },
};

const ephemeralBaseEnvironment = process.env.REACT_APP_EPHEMERAL_BASE_ENV
  ? defaults[process.env.REACT_APP_EPHEMERAL_BASE_ENV]
  : defaults.staging;

// Pass in an ephemeral hostname via the REACT_APP_REZNOR_URL command line variable
// ie `REACT_APP_REZNOR_URL=https://dev-reznor-pr-6376-api.clearcover.systems npm run start:ephemeral`
defaults.ephemeral = {
  ...ephemeralBaseEnvironment,
  agentTestPagesEnabled: true,
  rewardsSpinEnabled: true,
  rewardsMockEnabled: false,
  rewardsMockAct3: false,
  auth0ClientId: 'niPTxTZGcKHZXelRa1iLZIjijwX6elJU',
  auth0RedirectUrl: `http://app.localhost:3002/auth`,
  reznorHostname: `${process.env.REACT_APP_REZNOR_URL}`,
  jaggerUrl: `${process.env.REACT_APP_JAGGER_URL}/agent/apply`,
  // varysHostname: 'https://dev-coreapp-pr-2521-policyfe-api.clearcover.systems', //update when using an xl ephemeral
};

// A deployed build runs `vite build` with no REACT_APP_BACKEND, so activeEnvironment below is
// always defaults.local and window.env supplies the real values. That makes the `flag === '1' ||
// default` idiom used by the older flags unable to turn a default-true flag off — "0" loses to the
// default. These flags are environment kill switches, so an explicit window.env value has to win.
const envFlag = (raw: unknown, fallback: boolean): boolean =>
  isPresent(raw) ? raw === '1' || raw === 1 : fallback;

// Allows npm commands to determine which backend to use
// npm start -> runs against local backend
// npm run start:staging -> runs against staging backend
// npm run start:uat -> runs against uat backend
const activeEnvironment =
  process.env.REACT_APP_BACKEND &&
  defaults.hasOwnProperty(process.env.REACT_APP_BACKEND)
    ? defaults[process.env.REACT_APP_BACKEND]
    : defaults.local;

export const env: Env = isPresent(window.env)
  ? {
      auth0Domain: window.env.AUTH0_DOMAIN || activeEnvironment.auth0Domain,
      auth0ClientId:
        window.env.AUTH0_CLIENT_ID || activeEnvironment.auth0ClientId,
      auth0RedirectUrl:
        window.env.AUTH0_REDIRECT_URI || activeEnvironment.auth0RedirectUrl,
      auth0Audience:
        window.env.AUTH0_AUDIENCE || activeEnvironment.auth0Audience,
      reznorHostname:
        window.env.REZNOR_HOSTNAME || activeEnvironment.reznorHostname,
      ryersonHostname:
        window.env.RYERSON_HOSTNAME || activeEnvironment.ryersonHostname,
      ryersonWebHostname:
        window.env.RYERSON_WEB_HOSTNAME || activeEnvironment.ryersonWebHostname,
      varysHostname:
        window.env.VARYS_HOSTNAME || activeEnvironment.varysHostname,
      rewardsHostname:
        window.env.REWARDS_HOSTNAME || activeEnvironment.rewardsHostname,
      incentiveProgramsHostname:
        window.env.INCENTIVE_PROGRAMS_HOSTNAME ||
        activeEnvironment.incentiveProgramsHostname,
      pmwHostname: window.env.PMW_HOSTNAME || activeEnvironment.pmwHostname,
      sentryEnvironment:
        window.env.SENTRY_ENVIRONMENT || activeEnvironment.sentryEnvironment,
      fullstoryId: window.env.FULLSTORY_ID || activeEnvironment.fullstoryId,
      proconEnabled:
        window.env.PROCON_ENABLED === '1' || activeEnvironment.proconEnabled,
      adaChatEnabled:
        window.env.ADA_CHAT_ENABLED === '1' || activeEnvironment.adaChatEnabled,
      stradaOrganizationId:
        window.env.STRADA_ORGANIZATION_ID ||
        activeEnvironment.stradaOrganizationId,
      stradaAgentId:
        window.env.STRADA_AGENT_ID || activeEnvironment.stradaAgentId,
      datadogEnv: window.env.DATADOG_ENV || activeEnvironment.datadogEnv,
      agentTestPagesEnabled:
        window.env.AGENT_TEST_PAGES_ENABLED === '1' ||
        activeEnvironment.agentTestPagesEnabled,
      jaggerUrl: window.env.JAGGER_URL || activeEnvironment.jaggerUrl,
      adyenEnv: window.env.ADYEN_ENV || activeEnvironment.adyenEnv,
      adyenClientKey:
        window.env.ADYEN_CLIENT_KEY || activeEnvironment.adyenClientKey,
      adyenVersion: window.env.ADYEN_VERSION || activeEnvironment.adyenVersion,
      adyenCssHash: window.env.ADYEN_CSS_HASH || activeEnvironment.adyenCssHash,
      adyenScriptHash:
        window.env.ADYEN_SCRIPT_HASH || activeEnvironment.adyenScriptHash,
      adyenCardAvsMode:
        window.env.ADYEN_CARD_AVS_MODE || activeEnvironment.adyenCardAvsMode,
      smartyStreetsId:
        window.env.SMARTYSTREETS_ID || activeEnvironment.smartyStreetsId,
      stradaEnabled: envFlag(
        window.env.STRADA_ENABLED,
        activeEnvironment.stradaEnabled
      ),
      adasFeaturesEnabled: envFlag(
        window.env.ADAS_FEATURES_ENABLED,
        activeEnvironment.adasFeaturesEnabled
      ),
      partnerCommissionsAdminMenuEnabled: envFlag(
        window.env.PARTNER_COMMISSIONS_ADMIN_MENU_ENABLED,
        activeEnvironment.partnerCommissionsAdminMenuEnabled
      ),
      oneInc3ExternalLink:
        window.env.ONE_INC_3_EXTERNAL_LINK ||
        activeEnvironment.oneInc3ExternalLink,
      oneInc4ExternalLink:
        window.env.ONE_INC_4_EXTERNAL_LINK ||
        activeEnvironment.oneInc4ExternalLink,
      processOneScriptUrl:
        window.env.PROCESS_ONE_SCRIPT_URL ||
        activeEnvironment.processOneScriptUrl,
      versionNumber:
        window.env.VERSION_NUMBER || activeEnvironment.versionNumber,
      maintenancePageFlag:
        window.env.MAINTENANCE_PAGE_FLAG === '1' ||
        window.env.MAINTENANCE_PAGE_FLAG === 1 || //ephemerals
        activeEnvironment.maintenancePageFlag,
      survicateEnvironment:
        window.env.SURVICATE_ENVIRONMENT ||
        activeEnvironment.survicateEnvironment,
      rewardsCenterChallengesEnabled:
        window.env.REWARDS_CENTER_CHALLENGES_ENABLED === 1 ||
        window.env.REWARDS_CENTER_CHALLENGES_ENABLED === '1' ||
        activeEnvironment.rewardsCenterChallengesEnabled,
      rewardsSpinEnabled:
        window.env.REWARDS_SPIN_ENABLED === 1 ||
        window.env.REWARDS_SPIN_ENABLED === '1' ||
        activeEnvironment.rewardsSpinEnabled,
      rewardsMockEnabled:
        window.env.REWARDS_MOCK_ENABLED === 1 ||
        window.env.REWARDS_MOCK_ENABLED === '1' ||
        activeEnvironment.rewardsMockEnabled,
      rewardsMockAct3:
        window.env.REWARDS_MOCK_ACT3 === 1 ||
        window.env.REWARDS_MOCK_ACT3 === '1' ||
        activeEnvironment.rewardsMockAct3,
      vehicleNewQuestionsEnabled:
        window.env.VEHICLE_NEW_QUESTIONS_ENABLED === 1 ||
        window.env.VEHICLE_NEW_QUESTIONS_ENABLED === '1' ||
        activeEnvironment.vehicleNewQuestionsEnabled,
      driverNewQuestionsEnabled:
        window.env.DRIVER_NEW_QUESTIONS_ENABLED === 1 ||
        window.env.DRIVER_NEW_QUESTIONS_ENABLED === '1' ||
        activeEnvironment.driverNewQuestionsEnabled,
      updateCoveragesWithoutHealing:
        window.env.UPDATE_COVERAGES_WITHOUT_HEALING === '1' ||
        window.env.UPDATE_COVERAGES_WITHOUT_HEALING === 1 ||
        activeEnvironment.updateCoveragesWithoutHealing,
      paymentFlexibilityEveryTwoWeeks:
        window.env.PAYMENT_FLEXIBILITY_EVERY_TWO_WEEKS === '1' ||
        window.env.PAYMENT_FLEXIBILITY_EVERY_TWO_WEEKS === 1 ||
        activeEnvironment.paymentFlexibilityEveryTwoWeeks,
      paymentFlexibilityDownpaymentSlider:
        window.env.PAYMENT_FLEXIBILITY_DOWNPAYMENT_SLIDER === '1' ||
        window.env.PAYMENT_FLEXIBILITY_DOWNPAYMENT_SLIDER === 1 ||
        activeEnvironment.paymentFlexibilityDownpaymentSlider,
      paymentsAndBillingNewDesign:
        window.env.PAYMENTS_AND_BILLING_NEW_DESIGN === '1' ||
        window.env.PAYMENTS_AND_BILLING_NEW_DESIGN === 1 ||
        activeEnvironment.paymentsAndBillingNewDesign,
      independentAgentStates:
        window.env.INDEPENDENT_AGENT_STATES ||
        activeEnvironment.independentAgentStates,
    }
  : activeEnvironment;
